import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * @typedef {Object} RecommendationRailRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} count
 * @property {HTMLElement} progress
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 */

/**
 * Horizontal rail of Shopify product recommendations.
 * Stays hidden when the request returns no products.
 *
 * @extends {Component<RecommendationRailRefs>}
 */
class RecommendationRail extends Component {
  requiredRefs = ['track', 'count', 'progress', 'previous', 'next'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.track.addEventListener('scroll', () => this.#updateControls(), { passive: true, signal });
    window.addEventListener('resize', () => this.#updateControls(), { signal });

    if (this.dataset.source === 'collection' || this.dataset.recommendationsPerformed === 'true') {
      this.#updateControls();
      return;
    }

    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {Event} event
   */
  scrollPrevious(event) {
    event.preventDefault();
    this.#scroll(-1);
  }

  /**
   * @param {Event} event
   */
  scrollNext(event) {
    event.preventDefault();
    this.#scroll(1);
  }

  async #load() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const intent = this.dataset.intent || 'related';
    const baseUrl = this.dataset.url;

    if (!productId || !sectionId || !baseUrl) {
      if (this.dataset.designMode !== 'true') this.hidden = true;
      return;
    }

    this.setAttribute('aria-busy', 'true');

    const url = new URL(baseUrl, location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', intent);

    try {
      const response = await fetch(url, { signal: this.#abortController?.signal });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const html = await response.text();
      await morphSection(sectionId, html);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error(error);
      if (this.dataset.designMode !== 'true') this.hidden = true;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {number} direction
   */
  #scroll(direction) {
    const card = this.refs.track.querySelector('[data-recently-viewed-card]');
    if (!card) return;

    const styles = getComputedStyle(this.refs.track);
    const gap = Number.parseFloat(styles.columnGap) || 0;
    const amount = card.getBoundingClientRect().width + gap;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.refs.track.scrollBy({
      left: direction * amount,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #updateControls() {
    const track = this.refs.track;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollLeft = Math.abs(track.scrollLeft);
    const canScroll = maxScroll > 2;

    this.refs.previous.disabled = !canScroll || scrollLeft <= 2;
    this.refs.next.disabled = !canScroll || scrollLeft >= maxScroll - 2;
    this.classList.toggle('recently-viewed--overflow', canScroll);

    const seen = maxScroll <= 0 ? 1 : (scrollLeft + track.clientWidth) / track.scrollWidth;
    this.refs.progress.style.transform = `scaleX(${Math.min(1, Math.max(0, seen))})`;
  }
}

if (!customElements.get('recommendation-rail')) {
  customElements.define('recommendation-rail', RecommendationRail);
}
